/**
 * Insights — Phase 6A/6B/6C (Figma refresh).
 *
 * Four tabs: Overview · Spending · Income · Patterns.
 *
 * Phase 6C:
 *   • Clarity Score computation moved to lib/clarityScore.js — a client-side
 *     port of the authoritative backend compute_metrics(). 9 weighted
 *     components sum to 100, with stage gating (none/early/established).
 *   • Data hook now queries the correct schema: transactions.txn_date,
 *     recurring_expenses (not `recurring`), plus onboarding_profiles,
 *     wealth_holdings, and liabilities so the score has every input it
 *     needs (runway, debt awareness, investment visibility).
 *   • BORROWING_INCOME_CATEGORIES are excluded from income metrics.
 *   • Aggregations tolerate both `txn_date` (authoritative) and `date`
 *     (legacy alias) via lib/insightsSignals helpers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { formatMoney } from '@/lib/format';
import { fetchNetWorthBreakdown } from '@/lib/currency';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { colorForCategory } from '@/lib/categorize';
// BUG-002 — one shared income rule (category set + loan-drawdown narrations).
import { isRealIncome } from '@/lib/interimPolicy';
import { detectPatterns } from '@/lib/insightsSignals';
import { useInsightsData } from '@/hooks/useInsightsData';
import PatternsTabV2 from '@/components/insights/PatternsTabV2';
import ReportingCurrencyPrompt from '@/components/ReportingCurrencyPrompt';
import { computeClarityScore } from '@/lib/clarityScore';
import { RANGE_PRESETS, resolveRange, txnInRange } from '@/lib/dateRange';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'spending', label: 'Spending' },
  { id: 'income',   label: 'Income' },
  { id: 'patterns', label: 'Patterns' },
];

// ---------- helpers ----------
// Phase 6C — authoritative column is `txn_date` (schema.sql), but tolerate
// legacy `date` alias so cached rows / unit tests still parse.
function txDateOf(t) { return t?.txn_date || t?.date || null; }
function monthKey(iso) { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
function monthLabel(k) { const [y, m] = k.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short' }); }

function TimeRangeSelector({ value, onChange }) {
  const opts = [{ v: '1m', label: '1M' }, { v: '3m', label: '3M' }, { v: '6m', label: '6M' }, { v: '1y', label: '1Y' }];
  return (
    <div className="flex items-center gap-0.5 bg-offwhite border border-border rounded-lg p-0.5">
      {opts.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} data-testid={`insights-range-${o.v}`}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${value === o.v ? 'bg-white text-charcoal shadow-sm' : 'text-muted-foreground'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------- CategoryDonut ----------
// Phase 84.10 — Reusable donut for category-style breakdowns.
// Reused by SpendingTab (Spending by category) and IncomeTab (Income
// by source). Renders:
//   • a compact donut with a top-N + "Other" rollup so large-taxonomy
//     ledgers stay readable,
//   • a color legend to the right showing the top-6 by value,
//   • an "empty" state matching the tone of the surrounding cards.
// Deliberately additive — the existing horizontal-bar breakdowns below
// each chart remain untouched.
function CategoryDonut({ rows, title, subtitle, baseCurrency, testId }) {
  // Guard: nothing to render.
  if (!rows || rows.length === 0) return null;

  // Roll long-tail categories up so the donut doesn't fragment into
  // 15+ tiny slivers when the ledger has many fine-grained tags.
  const TOP_N = 8;
  const sorted = [...rows].sort((a, b) => b.amount - a.amount);
  let slices;
  if (sorted.length <= TOP_N) {
    slices = sorted;
  } else {
    const top = sorted.slice(0, TOP_N);
    const otherValue = sorted.slice(TOP_N).reduce((s, r) => s + r.amount, 0);
    slices = [...top];
    if (otherValue > 0) slices.push({ label: 'Other', amount: otherValue, _isRollup: true });
  }

  const total = slices.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">{title}</p>
          {subtitle ? (
            <p className="text-sm font-medium text-charcoal mt-0.5">{subtitle}</p>
          ) : null}
        </div>
        <p className="text-sm font-medium text-charcoal num shrink-0">
          {formatMoney(total, baseCurrency)}
        </p>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6 items-center">
        <div style={{ height: 200 }} className="min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="amount"
                nameKey="label"
                innerRadius={54}
                outerRadius={86}
                paddingAngle={2}
                stroke="hsl(var(--background))"
                strokeWidth={1}
                isAnimationActive={false}
              >
                {slices.map((s) => (
                  <Cell
                    key={s.label}
                    fill={s._isRollup ? '#94a3b8' : colorForCategory(s.label)}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, name) => [formatMoney(v, baseCurrency), name]}
                contentStyle={{
                  background: 'white',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  fontSize: 12,
                  padding: '8px 10px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <ul className="space-y-2 min-w-0">
          {slices.slice(0, 6).map((s) => {
            const pct = total > 0 ? (s.amount / total) * 100 : 0;
            const dotColor = s._isRollup ? '#94a3b8' : colorForCategory(s.label);
            return (
              <li key={s.label} className="flex items-center gap-3 text-sm">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: dotColor }}
                />
                <span className="flex-1 truncate text-charcoal">{s.label}</span>
                <span className="text-[11px] text-muted-foreground num shrink-0">
                  {pct.toFixed(1)}%
                </span>
                <span className="text-charcoal num font-medium shrink-0">
                  {formatMoney(s.amount, baseCurrency)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}


function ChartTooltip({ active, payload, label, baseCurrency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-xl px-3 py-2.5 shadow-lg text-xs">
      <p className="font-medium text-charcoal mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color || p.fill }} />
          <span className="text-muted-foreground capitalize">{p.name}:</span>
          <span className="font-medium text-charcoal num">{formatMoney(p.value, baseCurrency)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- Net Worth chart ----------
// Phase 84.20.3 (user #5) — Net Worth returned to the platform. Rendered
// as a full-width chart on the Insights → Overview tab.
//
// Data derivation:
//   • Current net worth = fetchNetWorthBreakdown() (existing API).
//   • Historical months = walk BACKWARDS through the user's txn cash flow,
//     subtracting net cash flow (income − spending) month by month.
//   • This is a first-order approximation (assumes cash-in accumulates in
//     assets and cash-out depletes them; ignores unrealised gains inside
//     wealth_holdings). Good enough for the Insights context, honest
//     enough not to overpromise. When we ship a proper snapshot table
//     (Phase 87 candidate), this hook swaps to the snapshot endpoint
//     with zero UI changes.
function useNetWorthTimeline(txns, session) {
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session?.access_token) return;
      try {
        const nw = await fetchNetWorthBreakdown(session.access_token);
        if (!cancelled) setCurrent(nw);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  return useMemo(() => {
    if (!current) return { series: [], current: null };
    const netToday = Number(current.net_worth || 0);

    // Build a per-month net cash flow map from the last 12 months of txns.
    const flowByMonth = new Map();
    txns.forEach((t) => {
      const d = txDateOf(t);
      if (!d) return;
      const k = monthKey(d);
      // Phase 84.23.4 — Net-worth back-fill uses `_baseAmount` so
      // mixed-currency flows collapse into the same base as the
      // starting net-worth value. Prevents AED-base users from seeing
      // the timeline drift when INR/USD txns are counted at native
      // scale.
      const amt = Math.abs(Number((t._baseAmount != null ? t._baseAmount : t.amount) || 0));
      if (!flowByMonth.has(k)) flowByMonth.set(k, { income: 0, spending: 0 });
      const row = flowByMonth.get(k);
      if (isRealIncome(t)) row.income += amt;
      else if (t.type === 'expense') row.spending += amt;
    });

    // Emit last 12 months oldest → newest. `net_worth[m]` is derived by
    // walking BACKWARDS from today: NW[m] = NW[m+1] − netFlow[m+1].
    const today = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        month: d.toLocaleDateString(undefined, { month: 'short' }),
      });
    }
    // Compute backwards.
    const series = [...months];
    // The last month = current NW.
    series[series.length - 1] = { ...series[series.length - 1], value: netToday };
    for (let i = series.length - 2; i >= 0; i--) {
      const nextKey = series[i + 1].key;
      const flow = flowByMonth.get(nextKey) || { income: 0, spending: 0 };
      const netFlowNext = flow.income - flow.spending;
      // NW[m] = NW[m+1] − netFlow[m+1]. If income was 10k and spending 8k
      // in the NEXT month, we grew by 2k, so previous month was 2k lower.
      const prev = (series[i + 1].value || 0) - netFlowNext;
      series[i] = { ...series[i], value: Math.round(prev) };
    }
    return { series, current };
  }, [current, txns]);
}

function NetWorthTooltip({ active, payload, label, baseCurrency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg border border-border px-3 py-2 shadow-md">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70">{label}</p>
      <p className="text-sm font-medium text-charcoal num mt-0.5">{formatMoney(payload[0].value, baseCurrency)}</p>
    </div>
  );
}

function NetWorthChart({ txns, session, baseCurrency, loading }) {
  const { series, current } = useNetWorthTimeline(txns, session);
  const latest = series[series.length - 1];
  const first = series[0];
  const delta = (latest?.value != null && first?.value != null) ? (latest.value - first.value) : 0;
  const deltaPct = (first?.value ? (delta / Math.abs(first.value)) * 100 : 0);
  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="insights-networth-chart">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">Net Worth</p>
          <p className="font-fraunces text-3xl font-medium text-charcoal tracking-tight num mt-1">
            {loading || !current ? 'Loading…' : formatMoney(current?.net_worth || 0, baseCurrency)}
          </p>
        </div>
        {current && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70">12-month change</p>
            <p className="text-sm font-medium num mt-0.5" style={{ color: delta >= 0 ? '#3D7A63' : '#A84E38' }}>
              {delta >= 0 ? '+' : '−'}{formatMoney(Math.abs(delta), baseCurrency)}
              <span className="ml-1.5 text-[11px] opacity-70">({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%)</span>
            </p>
          </div>
        )}
      </div>
      {current && (
        <p className="text-[11px] text-muted-foreground mb-3">
          Assets {formatMoney(current.total_assets || 0, baseCurrency)} · Liabilities {formatMoney(current.total_liabilities || 0, baseCurrency)}
        </p>
      )}
      <div style={{ height: 220, width: '100%', minHeight: 220 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#163A32" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#163A32" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 2" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip content={<NetWorthTooltip baseCurrency={baseCurrency} />} />
            <Area type="monotone" dataKey="value" stroke="#163A32" strokeWidth={2} fill="url(#nwGrad)" dot={{ r: 2, fill: '#163A32' }} name="Net Worth" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground/70 mt-3 italic">
        Historical months estimated from your monthly cash flow. Current position uses real balances from Wealth + Liabilities + Accounts.
      </p>
    </div>
  );
}

// ---------- Clarity Score (real, computed by lib/clarityScore) ----------
// Displays the authoritative 9-component score (0-100) via a 4-bucket
// visual (Coverage & Data / Cash flow / Commitments & Debt / Goals & Growth).
// When stage is 'none' or 'early', the number is shown muted with a "Building
// your clarity" / "Early estimate" label so we never over-promise on sparse data.
function ClarityScoreCard({ score }) {
  const {
    overall = 0,
    buckets = [],
    band = '—',
    deltaPts = 0,
    stage = 'established',
    confidence = 'high',
  } = score || {};
  const muted = stage !== 'established';
  const stageLabel = stage === 'none' ? 'Building your clarity'
                   : stage === 'early' ? 'Early estimate'
                   : null;
  return (
    <div className="bg-evergreen rounded-2xl p-6 text-white" data-testid="insights-clarity">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">Clarity Score</p>
        {stageLabel && (
          <span
            className="text-[10px] font-semibold tracking-widest uppercase px-2 py-1 rounded-full bg-white/10 text-white/60"
            data-testid="insights-clarity-stage"
            title={`Confidence: ${confidence}`}
          >
            {stageLabel}
          </span>
        )}
      </div>
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <p
            className={`font-fraunces text-6xl font-medium leading-none tracking-tight num ${muted ? 'text-white/50' : ''}`}
            data-testid="insights-clarity-score"
          >
            {stage === 'none' ? '—' : overall}
          </p>
          <p className="text-sage-light text-sm mt-2 font-medium" data-testid="insights-clarity-band">{band}</p>
          {deltaPts !== 0 && stage === 'established' && (
            <p className="text-white/40 text-xs mt-1">
              {deltaPts >= 0 ? 'Up' : 'Down'} {Math.abs(deltaPts)} point{Math.abs(deltaPts) === 1 ? '' : 's'} vs last month
            </p>
          )}
        </div>
        <div className="flex-1 min-w-[220px] space-y-3">
          {buckets.map((d) => (
            <div key={d.label} data-testid={`insights-clarity-bucket-${d.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/50 text-[11px]">{d.label}</span>
                <span className="text-white/70 text-[11px] font-medium num">{d.score}</span>
              </div>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: d.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- data hook: useInsightsData was extracted to
// /hooks/useInsightsData.js in v3.4.7 so components outside this page
// (Settings → Life Profile Clarity Score embed) can reuse the same
// data-fetch. The rest of the module still imports and uses the same
// exported hook. ----------

function rollupByMonth(txns) {
  const map = new Map();
  txns.forEach((t) => {
    const iso = txDateOf(t);
    if (!iso) return;
    const k = monthKey(iso);
    if (!map.has(k)) map.set(k, { key: k, month: monthLabel(k), income: 0, spending: 0, saved: 0 });
    const row = map.get(k);
    // Phase 84.22.7 — Use `_baseAmount` (FX-converted at load time) so
    // mixed-currency txns don't get summed as if they were all base.
    // Falls back to native `amount` for legacy callers pre-enrichment.
    const amt = Number((t._baseAmount != null ? t._baseAmount : t.amount) || 0);
    // Exclude borrowing categories from "income" so the Insights totals
    // (and the derived surplus) match the Clarity Score / backend semantics.
    if (isRealIncome(t)) row.income += Math.abs(amt);
    else if (t.type === 'expense') row.spending += Math.abs(amt);
  });
  const rows = Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  rows.forEach((r) => { r.saved = Math.max(0, r.income - r.spending); r.spent = r.spending; });
  return rows;
}

function filterByRange(rows, range) {
  const n = range === '1m' ? 1 : range === '3m' ? 3 : range === '6m' ? 6 : 12;
  return rows.slice(-n);
}

// ---------- Overview ----------
function OverviewTab({ txns, loading, baseCurrency, session, onNavigateToTab, currencyCtx }) {
  const [range, setRange] = useState('6m');
  const rollup = useMemo(() => rollupByMonth(txns), [txns]);
  const visible = useMemo(() => filterByRange(rollup, range), [rollup, range]);
  const latest = visible[visible.length - 1];
  const prev   = visible[visible.length - 2];

  const spendingThisMonth = latest?.spending || 0;
  const incomeThisMonth   = latest?.income   || 0;
  // Phase 84.22.7 — surplus can go negative when spending outpaces
  // income in the month. Previously `Math.max(0, …)` masked that with
  // "AED 0", which read as "you saved nothing" when the truth was
  // "you overspent". A negative surplus is signal, not noise.
  const surplus           = incomeThisMonth - spendingThisMonth;
  const spendingChangePct = prev?.spending ? ((spendingThisMonth - prev.spending) / prev.spending) * 100 : 0;
  const surplusPrev = prev ? (prev.income - prev.spending) : 0;
  const surplusChangePct = surplusPrev ? ((surplus - surplusPrev) / Math.abs(surplusPrev)) * 100 : 0;

  // Category breakdown for the last month (aggregated in base currency
  // so mixing INR / AED / USD spend gives an apples-to-apples pie).
  const catBreakdown = useMemo(() => {
    const lastKey = latest?.key;
    if (!lastKey) return [];
    const rows = txns.filter((t) => { const d = txDateOf(t); return d && monthKey(d) === lastKey && t.type === 'expense'; });
    const byCat = {};
    rows.forEach((t) => {
      const cat = t.category || 'Miscellaneous';
      // Phase 84.23.4 — Convert LIVE via currencyCtx (see SpendingTab).
      const cur = currencyCtx?.currencyOfAccount
        ? currencyCtx.currencyOfAccount(t.account_id)
        : (t._currency || baseCurrency);
      const amt = currencyCtx?.toBase
        ? currencyCtx.toBase(Number(t.amount || 0), cur)
        : Number((t._baseAmount != null ? t._baseAmount : t.amount) || 0);
      byCat[cat] = (byCat[cat] || 0) + Math.abs(amt);
    });
    const total = Object.values(byCat).reduce((s, v) => s + v, 0);
    return Object.entries(byCat)
      .map(([label, amount]) => ({ label, amount, pct: total > 0 ? (amount / total) * 100 : 0 }))
  }, [txns, latest, currencyCtx, baseCurrency]);

  // Top merchants for the last month. A merchant is almost always
  // single-currency (Marina Terrace rent is AED, HDFC HL EMI is INR),
  // so we surface the merchant's NATIVE currency + amount. When a
  // merchant DOES span currencies (rare — e.g. a Careem trip billed in
  // both USD and INR the same month), we fall back to base-currency
  // aggregation with a clear label. This matches how Transactions and
  // Recurring pages render per-row amounts.
  const topMerchants = useMemo(() => {
    const lastKey = latest?.key;
    if (!lastKey) return [];
    const rows = txns.filter((t) => { const d = txDateOf(t); return d && monthKey(d) === lastKey && t.type === 'expense'; });
    const map = {};
    rows.forEach((t) => {
      const key = (t.description || 'Unknown').trim().slice(0, 32);
      if (!map[key]) {
        map[key] = {
          name: key,
          category: t.category || 'Uncategorised',
          native_amount: 0,
          currency: t._currency || baseCurrency,
          base_amount: 0,
          mixedCurrency: false,
          txns: 0,
        };
      }
      const row = map[key];
      if (row.currency !== (t._currency || baseCurrency)) row.mixedCurrency = true;
      row.native_amount += Math.abs(Number(t.amount) || 0);
      row.base_amount += Math.abs(Number(t._baseAmount != null ? t._baseAmount : t.amount) || 0);
      row.txns += 1;
    });
    return Object.values(map)
      .map((r) => ({
        ...r,
        // When a merchant only ever bills in one currency, the top-line
        // amount shows that native currency. If mixed, fall back to base
        // for a clean comparison.
        amount: r.mixedCurrency ? r.base_amount : r.native_amount,
        display_currency: r.mixedCurrency ? baseCurrency : r.currency,
      }))
      .sort((a, b) => b.base_amount - a.base_amount)
      .slice(0, 5);
  }, [txns, latest, baseCurrency]);

  return (
    <div className="space-y-6" data-testid="insights-tab-overview">
      {/* Phase 84.20.3 — Net Worth chart replaces the Clarity Score card
          in the top slot on Overview. Clarity Score now lives on the
          Patterns tab. */}
      <NetWorthChart txns={txns} session={session} baseCurrency={baseCurrency} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Monthly income',   value: incomeThisMonth,   sub: 'This month',    up: null },
          { label: 'Monthly spending', value: spendingThisMonth, sub: `${spendingChangePct >= 0 ? '↑' : '↓'} ${Math.abs(spendingChangePct).toFixed(1)}% from last month`, up: spendingChangePct <= 0 },
          { label: 'Monthly surplus',  value: surplus,           sub: `${surplusChangePct >= 0 ? '↑' : '↓'} ${Math.abs(surplusChangePct).toFixed(1)}% from last month`, up: surplusChangePct >= 0 },
          { label: 'Total transactions', value: null,             sub: `${txns.length} in last 12 months`, up: null },
        ].map((m) => (
          <div key={m.label} className="bg-white rounded-xl border border-border p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2">{m.label}</p>
            <p className="font-fraunces text-2xl font-medium text-charcoal tracking-tight num">
              {m.value != null ? formatMoney(m.value, baseCurrency) : txns.length}
            </p>
            <p className="text-[11px] mt-1.5" style={{ color: m.up === true ? '#3D7A63' : m.up === false ? '#A84E38' : '#9B9890' }}>{m.sub}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">Spending vs Income</p>
            <p className="text-sm font-medium text-charcoal mt-0.5">{loading ? 'Loading…' : `${formatMoney(spendingThisMonth, baseCurrency)} spent this month`}</p>
          </div>
          <TimeRangeSelector value={range} onChange={setRange} />
        </div>
        <div style={{ height: 200, width: '100%', minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={visible} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#163A32" stopOpacity={0.10} /><stop offset="95%" stopColor="#163A32" stopOpacity={0} /></linearGradient>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#D4A95D" stopOpacity={0.18} /><stop offset="95%" stopColor="#D4A95D" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 2" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip baseCurrency={baseCurrency} />} />
              <Area type="monotone" dataKey="income"   stroke="#163A32" strokeWidth={1.6} fill="url(#incGrad)"   dot={false} name="Income" />
              <Area type="monotone" dataKey="spending" stroke="#D4A95D" strokeWidth={1.6} fill="url(#spendGrad)" dot={false} name="Spending" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">Spending by Category</p>
          {/* Phase 84.20.3 — Bridge to the Spending sub-tab for the full
              breakdown, filter controls, and drill-down list. */}
          <button
            type="button"
            onClick={() => onNavigateToTab?.('spending')}
            className="text-[11px] font-medium text-[hsl(var(--primary))] hover:underline underline-offset-2"
            data-testid="insights-overview-see-more-spending"
          >
            See more →
          </button>
        </div>
        {catBreakdown.length === 0 ? (
          <p className="text-sm text-muted-foreground">No spend recorded this month.</p>
        ) : (
          <div className="space-y-2.5">
            {catBreakdown.slice(0, 5).map((cat) => (
              <div key={cat.label} className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorForCategory(cat.label) }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-charcoal">{cat.label}</span>
                    <span className="text-xs text-muted-foreground num">{formatMoney(cat.amount, baseCurrency)}</span>
                  </div>
                  <div className="h-1 bg-offwhite rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${cat.pct}%`, backgroundColor: colorForCategory(cat.label), opacity: 0.7 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {topMerchants.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-6">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Top Merchants this month</p>
          <ul className="divide-y divide-border/40">
            {topMerchants.map((m) => (
              <li key={m.name} className="flex items-center gap-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-offwhite flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold text-charcoal/40">{m.name[0]?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.category} · {m.txns} transaction{m.txns !== 1 ? 's' : ''}</p>
                </div>
                <p className="text-sm font-medium text-charcoal num shrink-0">{formatMoney(m.amount, m.display_currency || baseCurrency)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- Spending ----------
function SpendingTab({ txns, baseCurrency, currencyCtx }) {
  const [range, setRange] = useState('3m');
  // Phase 84.20.3 (user #3) — Date-range filter for the "Spending by
  // category" section (donut + all-categories list). Mirrors the same
  // preset set used on the Transactions page so the user's mental model
  // stays consistent. Defaults to `this_month` because that matches the
  // legacy "This month" label the section always showed.
  const [rangePreset, setRangePreset] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dateWindow = useMemo(
    () => resolveRange(rangePreset, customStart, customEnd),
    [rangePreset, customStart, customEnd],
  );

  const rollup = useMemo(() => rollupByMonth(txns), [txns]);
  const visible = useMemo(() => filterByRange(rollup, range), [rollup, range]);
  const latestKey = rollup[rollup.length - 1]?.key;

  // Categorised rows within the selected date window (independent of
  // `range`, which drives the monthly bar chart above).
  const catRows = useMemo(() => {
    const { from, to } = dateWindow;
    const inWindow = txns.filter((t) => {
      if (t.type !== 'expense') return false;
      const d = txDateOf(t);
      if (!d) return false;
      return txnInRange(d, from, to);
    });
    // Comparison window = one preset "step" earlier (crude but works for
    // showing the ↑/↓ percentage). For "this_month" → previous calendar
    // month. For everything else → the same-length period immediately
    // preceding `from`.
    const prevRows = (() => {
      if (rangePreset === 'this_month') {
        const [yy, mm] = (from || '').split('-').map(Number);
        if (!yy) return [];
        const prevStart = new Date(yy, mm - 2, 1);
        const prevEnd   = new Date(yy, mm - 1, 0);
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const pf = iso(prevStart); const pt = iso(prevEnd);
        return txns.filter((t) => t.type === 'expense' && txnInRange(txDateOf(t), pf, pt));
      }
      // For everything else, skip comparison for now (avoids flaky trend numbers).
      return [];
    })();

    // Phase 84.23.4 — Aggregate in BASE currency by converting each txn
    // LIVE via currencyCtx. This bypasses the snapshot `_baseAmount`
    // enrichment (which could be identity if rates hadn't loaded yet
    // when useInsightsData ran) and always uses the freshest FX rates
    // and account_currency_map from useCurrencyContext.
    const bucket = (arr) => arr.reduce((acc, t) => {
      const cat = t.category || 'Miscellaneous';
      const cur = currencyCtx?.currencyOfAccount
        ? currencyCtx.currencyOfAccount(t.account_id)
        : (t._currency || baseCurrency);
      const amtBase = currencyCtx?.toBase
        ? currencyCtx.toBase(Number(t.amount || 0), cur)
        : Number((t._baseAmount != null ? t._baseAmount : t.amount) || 0);
      acc[cat] = (acc[cat] || 0) + Math.abs(amtBase);
      return acc;
    }, {});
    const cur = bucket(inWindow); const prv = bucket(prevRows);
    const total = Object.values(cur).reduce((s, v) => s + v, 0);
    return Object.entries(cur)
      .map(([label, amount]) => ({
        label, amount,
        pct: total > 0 ? (amount / total) * 100 : 0,
        trend: prv[label] ? ((amount - prv[label]) / prv[label]) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [txns, dateWindow, rangePreset, currencyCtx, baseCurrency]);

  // Friendly label for the header. When the user picks a preset we show
  // the preset label; for custom we show "from - to".
  const rangeLabel = useMemo(() => {
    if (rangePreset === 'custom') {
      if (customStart && customEnd) return `${customStart} → ${customEnd}`;
      if (customStart) return `From ${customStart}`;
      if (customEnd)   return `Until ${customEnd}`;
      return 'Custom';
    }
    return RANGE_PRESETS.find((p) => p.value === rangePreset)?.label || 'This month';
  }, [rangePreset, customStart, customEnd]);

  return (
    <div className="space-y-6" data-testid="insights-tab-spending">
      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">Monthly Cash Flow</p>
            {/* Phase 84.20.3 — Label matched the bars: chart plots Spent
                (grey) + Saved (evergreen), not raw income vs spending. */}
            <p className="text-sm font-medium text-charcoal mt-0.5">Spent vs saved, month by month</p>
          </div>
          <TimeRangeSelector value={range} onChange={setRange} />
        </div>
        <div style={{ height: 200, width: '100%', minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={visible} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 2" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip baseCurrency={baseCurrency} />} />
              <Bar dataKey="spent" name="Spent" radius={[3, 3, 0, 0]}>
                {visible.map((_, i) => <Cell key={i} fill={i === visible.length - 1 ? '#D4A95D40' : '#E5E2DB'} />)}
              </Bar>
              <Bar dataKey="saved" name="Saved" radius={[3, 3, 0, 0]}>
                {visible.map((_, i) => <Cell key={i} fill={i === visible.length - 1 ? '#163A32' : '#163A3260'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Phase 84.20.3 (user #3) — Date-range picker that scopes the
          Spending-by-category donut + All-categories list. Uses the same
          preset set as the Transactions page filter for consistency. The
          "Monthly Cash Flow" chart above intentionally keeps its own
          1M/3M/6M/1Y toggle because it's a per-month rollup, not a
          per-txn filter. */}
      <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3 flex-wrap" data-testid="insights-spending-range">
        <p className="text-[11px] font-medium text-muted-foreground shrink-0">Spending by category · <span className="text-charcoal">{rangeLabel}</span></p>
        <div className="ml-auto flex items-center gap-2">
          <Select value={rangePreset} onValueChange={setRangePreset}>
            <SelectTrigger className="rounded-xl h-8 text-xs w-40" data-testid="insights-spending-range-select">
              <SelectValue placeholder="Range" />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`insights-spending-range-${p.value}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {rangePreset === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8 text-xs px-2 rounded-xl border border-border bg-white"
                data-testid="insights-spending-range-from"
                aria-label="From date"
              />
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8 text-xs px-2 rounded-xl border border-border bg-white"
                data-testid="insights-spending-range-to"
                aria-label="To date"
              />
            </>
          )}
        </div>
      </div>

      {/* Phase 84.10 — Spending-by-category donut. Uses `catRows` already
          computed above for the horizontal-bar breakdown; the donut sits
          right below the Monthly Cash Flow card and above the "All
          Categories" list so the reader can eyeball the mix quickly and
          then drill down to the sorted list below. */}
      <CategoryDonut
        rows={catRows}
        title="Spending by category"
        subtitle={`Mix for ${rangeLabel}`}
        baseCurrency={baseCurrency}
        testId="insights-spending-donut"
      />

      <div className="bg-white rounded-2xl border border-border p-6" data-testid="insights-all-categories">
        <div className="flex items-baseline justify-between mb-5 gap-3 flex-wrap">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">All Categories — {rangeLabel}</p>
          {(dateWindow.from || dateWindow.to) && (
            <Link
              to={`/transactions?type=expense${dateWindow.from ? `&from=${dateWindow.from}` : ''}${dateWindow.to ? `&to=${dateWindow.to}` : ''}`}
              className="text-[11px] font-medium text-primary hover:underline underline-offset-2"
              data-testid="insights-see-all-categories-link"
            >
              See all in Transactions →
            </Link>
          )}
        </div>
        {catRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No expense data for this period.</p>
        ) : (
          <div className="space-y-1">
            {catRows.map((cat) => (
              <Link
                key={cat.label}
                to={`/transactions?category=${encodeURIComponent(cat.label)}&type=expense${dateWindow.from ? `&from=${dateWindow.from}` : ''}${dateWindow.to ? `&to=${dateWindow.to}` : ''}`}
                className="flex items-center gap-4 px-3 py-3.5 rounded-xl hover:bg-offwhite transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-1"
                data-testid={`insights-category-row-${cat.label.replace(/\s+/g, '-').toLowerCase()}`}
                aria-label={`Open ${cat.label} transactions for ${rangeLabel}`}
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colorForCategory(cat.label) }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium text-charcoal">{cat.label}</span>
                    <span className="text-sm font-medium text-charcoal num">{formatMoney(cat.amount, baseCurrency)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <div className="flex-1 h-1 bg-offwhite rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${cat.pct}%`, backgroundColor: colorForCategory(cat.label), opacity: 0.6 }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0 num">{cat.pct.toFixed(1)}%</span>
                    {cat.trend !== 0 && (
                      <span className="text-[10px] font-medium shrink-0 num" style={{ color: cat.trend > 0 ? '#A84E38' : '#3D7A63' }}>
                        {cat.trend > 0 ? '↑' : '↓'}{Math.abs(cat.trend).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Income ----------
function IncomeTab({ txns, baseCurrency, currencyCtx }) {
  const rollup = useMemo(() => rollupByMonth(txns), [txns]);
  const latest = rollup[rollup.length - 1];
  const latestKey = latest?.key;

  const incomeSources = useMemo(() => {
    if (!latestKey) return [];
    const rows = txns.filter((t) => { const d = txDateOf(t); return d && monthKey(d) === latestKey && isRealIncome(t); });
    const map = {};
    rows.forEach((t) => {
      const c = t.category || 'Salary';
      // Phase 84.23.4 — Convert LIVE via currencyCtx (see SpendingTab
      // for rationale). Snapshot `_baseAmount` may be identity if
      // rates loaded after enrichment.
      const cur = currencyCtx?.currencyOfAccount
        ? currencyCtx.currencyOfAccount(t.account_id)
        : (t._currency || baseCurrency);
      const amtBase = currencyCtx?.toBase
        ? currencyCtx.toBase(Number(t.amount || 0), cur)
        : Number((t._baseAmount != null ? t._baseAmount : t.amount) || 0);
      map[c] = (map[c] || 0) + Math.abs(amtBase);
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map)
      .map(([label, amount]) => ({ label, amount, pct: total > 0 ? (amount / total) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount);
  }, [txns, latestKey, currencyCtx, baseCurrency]);

  return (
    <div className="space-y-6" data-testid="insights-tab-income">
      <div className="bg-evergreen rounded-2xl p-6 text-white">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40 mb-3">Total Income — this month</p>
        <p className="font-fraunces text-4xl font-medium tracking-tight num">{formatMoney(latest?.income || 0, baseCurrency)}</p>
        <p className="text-sage-light text-sm mt-1.5">{rollup.length > 1 ? 'Rolling 6-month view below' : 'Add transactions to see trends'}</p>
      </div>

      {/* Phase 84.10 — Income-by-source donut, placed ABOVE the existing
          "Income Sources" horizontal bars per user request. Reuses the
          same `incomeSources` array so the two visuals stay in sync. */}
      <CategoryDonut
        rows={incomeSources}
        title="Income by source"
        subtitle={latest?.month ? `Mix for ${latest.month}` : 'This month'}
        baseCurrency={baseCurrency}
        testId="insights-income-donut"
      />

      <div className="bg-white rounded-2xl border border-border p-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">Income Sources</p>
        {incomeSources.length === 0 ? (
          <p className="text-sm text-muted-foreground">No income transactions recorded this month.</p>
        ) : (
          <div className="space-y-4">
            {incomeSources.map((src) => (
              <div key={src.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorForCategory(src.label) }} />
                    <span className="text-sm font-medium text-charcoal">{src.label}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-charcoal num">{formatMoney(src.amount, baseCurrency)}</span>
                    <span className="text-[11px] text-muted-foreground ml-2 num">{src.pct.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="h-1.5 bg-offwhite rounded-full overflow-hidden ml-5">
                  <div className="h-full rounded-full" style={{ width: `${src.pct}%`, backgroundColor: colorForCategory(src.label) }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border p-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">Income Trend</p>
        <div style={{ height: 180, width: '100%', minHeight: 180 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={rollup} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="incGrad2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#163A32" stopOpacity={0.12} /><stop offset="95%" stopColor="#163A32" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 2" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip baseCurrency={baseCurrency} />} />
              <Area type="monotone" dataKey="income" name="Income" stroke="#163A32" strokeWidth={1.6} fill="url(#incGrad2)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ---------- Patterns (real, powered by detectPatterns) ----------
function PatternsTab({ patterns, score }) {
  return (
    <div className="space-y-6" data-testid="insights-tab-patterns">
      {/* Phase 84.20.3 (user #5) — Clarity Score card relocated here from
          the Overview tab. Users can now see the score alongside the
          pattern signals that feed into it, which is a more intuitive
          grouping. */}
      <ClarityScoreCard score={score} />

      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-border p-6">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-1">How 2120 identifies patterns</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Patterns are identified from your transaction history over 3–6 months. They are shown when a trend is consistent and potentially meaningful — not to alarm, but to inform.
          </p>
        </div>
        {patterns.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="px-6 pt-5 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 shrink-0" style={{ backgroundColor: p.bg }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal leading-snug">{p.title}</p>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{p.detail}</p>
                  {p.action && (
                    <button className="mt-3 text-xs font-medium hover:opacity-70 transition-opacity" style={{ color: p.accent }}>
                      {p.action} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Page ----------
export default function Insights() {
  usePageTitle('Insights');
  const { profile, user, session, refreshProfile } = useAuth();
  // Phase 84.22.9 (Wave B2) — currency context is the source of truth.
  // The old `profile.base_currency` reads are kept as a fallback for
  // the split second before the /api/currency/context call resolves.
  const currencyCtx = useCurrencyContext();
  const baseCurrency = currencyCtx.isReady
    ? currencyCtx.base
    // `profiles.currency` is a database default, not a decision, so it may
    // not name a consolidated total. Only `base_currency` records a choice.
    : (profile?.base_currency || null);
  // Every figure on this page is a combined one — a net-worth line, a monthly
  // total, a category donut. When the reporting currency was never chosen AND
  // the records span more than one currency (or there are no records at all),
  // there is no honest unit to state them in, so we ask rather than pick.
  const currencyUnstated = !profile?.base_currency;
  const withholdTotals = currencyUnstated && currencyCtx.isReady
    && !currencyCtx.canConsolidate;
  const [tab, setTab] = useState('overview');
  const { txns, goals, recurring, onboarding, holdings, liabilities, loading } = useInsightsData(user?.id, currencyCtx);
  // Authoritative Clarity Score — mirrors backend compute_metrics().
  const score = useMemo(
    () => computeClarityScore({ txns, onboarding, holdings, liabilities, goals, recurring, profile }),
    [txns, onboarding, holdings, liabilities, goals, recurring, profile],
  );
  const patterns = useMemo(() => detectPatterns({ txns, baseCurrency }),                    [txns, baseCurrency]);

  return (
    <div className="min-h-full" data-testid="insights-page">
      <div className="px-6 lg:px-10 pt-8 pb-6 border-b border-border bg-white">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-1">Awareness</p>
        <h1 className="font-fraunces text-2xl lg:text-3xl font-medium text-charcoal tracking-tight">Insights</h1>
        <p className="text-sm text-muted-foreground mt-1">Patterns, trends, and what your numbers mean.</p>
        <div className="flex items-stretch gap-0 mt-6 -mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`insights-tab-${t.id}-button`}
              className={`px-4 py-3 text-sm font-medium relative transition-colors shrink-0 whitespace-nowrap ${tab === t.id ? 'text-evergreen' : 'text-muted-foreground'}`}>
              {t.label}
              {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-evergreen rounded-t-sm" />}
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 lg:px-10 py-8 max-w-3xl">
        {currencyUnstated && (
          <div className="mb-6">
            <ReportingCurrencyPrompt
              profile={profile}
              token={session?.access_token}
              onSaved={() => { refreshProfile?.(); currencyCtx.reload?.(); }}
            />
          </div>
        )}
        {withholdTotals ? (
          <div
            className="bg-card rounded-xl border border-border p-6 text-center"
            data-testid="insights-totals-withheld"
          >
            <p className="text-sm font-medium text-charcoal mb-1">
              {currencyCtx.dataCurrencies.length > 1
                ? 'Choose a currency and these figures appear'
                : 'Nothing to show yet'}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto">
              {currencyCtx.dataCurrencies.length > 1
                ? `Your records span ${currencyCtx.dataCurrencies.join(', ')}. Combining
                   them into one number means picking the currency to state it in,
                   and that is your decision to make, not ours.`
                : `Once you add an account or import a statement, your patterns and
                   trends will be built from it.`}
            </p>
          </div>
        ) : (
          <>
            {tab === 'overview' && <OverviewTab txns={txns} loading={loading} baseCurrency={baseCurrency} session={session} onNavigateToTab={setTab} currencyCtx={currencyCtx} />}
            {tab === 'spending' && <SpendingTab txns={txns} baseCurrency={baseCurrency} currencyCtx={currencyCtx} />}
            {tab === 'income'   && <IncomeTab   txns={txns} baseCurrency={baseCurrency} currencyCtx={currencyCtx} />}
            {tab === 'patterns' && <PatternsTabV2 baseCurrency={baseCurrency} />}
          </>
        )}
      </div>
    </div>
  );
}

